import { useState, useMemo, useEffect, useCallback } from "react";
import { Search, ChevronDown, ChevronRight, Plus, Trash2, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  suchWoerter, markiere, kurzLang, passt, trefferInPositionen, alleStichwoerter, hatStichwort,
  sortiere, SORTIERUNGEN, euro, zahl, datumKurz, gruppeVon, norm,
} from "@/lib/bausteine";

/**
 * Bausteine – EINE Ansicht für alle Orte (Ralph 30.10.2026, „Profi-Version“):
 *   modus "leiste"         Leiste links im Beleg (Breite ziehbar, macht der Rahmen)
 *   modus "fenster"        eigenes Fenster /bausteine: Liste links, Vergleich (bis 3) rechts
 *   modus "handy"          ganze Seite am Handy (Vergleich untereinander, höchstens 2)
 *   modus "einstellungen"  Reiter „Bausteine“ in den Einstellungen
 * Es zählt der TEXTINHALT: Suche über Name, Kurztext, Langtext, Stichwörter; Treffer gelb; Positionen vollständig (nichts abgeschnitten).
 * Props: bloecke (sonst lädt die Ansicht selbst), onNeuLaden(liste), onEinfuegen(block, positionen) -> true/false,
 *        ziel ({ label } im Fenster: „+ In Angebot A-…“), onEigenesFenster (Knopf nur in der Leiste).
 */

const Markiert = ({ text, woerter }) => markiere(text, woerter).map((s, i) => (
  s.m ? <mark key={i} className="bg-yellow-200 text-black rounded-sm px-0.5" data-testid="treffer">{s.t}</mark> : <span key={i}>{s.t}</span>
));

const Stichwort = ({ w, aktiv, onClick, testid, klein }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`${klein ? "px-1.5 py-0 text-[10px]" : "px-2 py-0.5 text-xs"} rounded-full border transition-colors ${aktiv ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-primary/40 hover:bg-primary/10"}`}>
    {w}
  </button>
);

// ---------------------------------------------------------------- Positionen mit Haken (Kurz- UND Langtext vollständig)
// ohneHaken: reine Ansicht ohne Auswahlkästchen (genutzt von der Vorlagen-Vorschau „Ansehen“, eine Darstellung für beide)
export const Inhalt = ({ b, woerter, haken, setHaken, ohneHaken = false }) => {
  const pos = b.positions || [];
  const ist = (i) => !haken || haken.has(i);
  const alle = () => new Set(pos.map((_, i) => i));
  const umschalten = (i) => {
    const neu = new Set(haken || alle());
    if (pos[i].type === "titel") {
      const gruppe = gruppeVon(pos, i);
      const alleDrin = gruppe.every((x) => neu.has(x));
      gruppe.forEach((x) => (alleDrin ? neu.delete(x) : neu.add(x)));
    } else if (neu.has(i)) neu.delete(i); else neu.add(i);
    setHaken(neu);
  };
  return (
    <div className="mt-2" data-testid={`inhalt-${b.id}`}>
      {pos.map((p, i) => {
        const { kurz, lang } = kurzLang(p.description);
        if (p.type === "titel") {
          return (
            <label key={i} data-testid={ohneHaken ? "tpl-ansehen-titel" : undefined} className="flex items-center gap-2 py-1.5 px-1 mt-1 bg-amber-100/70 dark:bg-amber-900/30 rounded-sm cursor-pointer">
              {!ohneHaken && <input type="checkbox" checked={ist(i)} onChange={() => umschalten(i)} data-testid={`haken-${b.id}-${i}`} className="shrink-0 w-4 h-4" />}
              <span className="text-sm font-bold break-words min-w-0"><Markiert text={p.description} woerter={woerter} /></span>
            </label>
          );
        }
        return (
          <div key={i} data-testid={ohneHaken ? "tpl-ansehen-zeile" : undefined} className="flex items-start gap-2 py-1.5 px-1 border-b last:border-0 border-border/60">
            {!ohneHaken && <input type="checkbox" checked={ist(i)} onChange={() => umschalten(i)} data-testid={`haken-${b.id}-${i}`} className="mt-1 shrink-0 w-4 h-4" />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium break-words"><Markiert text={kurz} woerter={woerter} /></p>
              {lang && <p className="text-xs text-foreground/80 whitespace-pre-wrap break-words mt-0.5" data-testid="langtext"><Markiert text={lang} woerter={woerter} /></p>}
            </div>
            <div className="text-right text-xs whitespace-nowrap font-mono shrink-0">
              <div>{zahl(p.quantity)} {p.unit}</div>
              <div className="text-muted-foreground">{euro(p.price_net)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------- Bearbeiten: Name, Stichwörter, Positionen, Löschen
const Bearbeiten = ({ b, alleWoerter, onFertig, onGespeichert, onLoeschen }) => {
  const [name, setName] = useState(b.name || "");
  const [stich, setStich] = useState(b.stichwoerter || []);
  const [neu, setNeu] = useState("");
  const [pos, setPos] = useState(() => (b.positions || []).map((p) => ({ ...p })));
  const [busy, setBusy] = useState(false);
  const stichZu = (txt) => {
    const t = txt.trim();
    if (t && !stich.some((w) => norm(w) === norm(t))) setStich([...stich, t]);
    setNeu("");
  };
  const aendere = (i, feld, wert) => setPos(pos.map((p, k) => (k === i ? { ...p, [feld]: wert } : p)));
  const speichern = async () => {
    if (!name.trim()) { toast.warning("Bitte einen Namen eintragen"); return; }
    setBusy(true);
    try {
      const wörter = neu.trim() && !stich.some((w) => norm(w) === norm(neu)) ? [...stich, neu.trim()] : stich;
      await api.put(`/leistungsbloecke/${b.id}`, { name: name.trim(), stichwoerter: wörter, positions: pos });
      toast.success("Baustein gespeichert");
      onGespeichert?.();
      onFertig();
    } catch { toast.error("Fehler beim Speichern"); }
    setBusy(false);
  };
  return (
    <div className="mt-2 space-y-3 border rounded-sm p-3 bg-muted/30" data-testid={`bearbeiten-${b.id}`}>
      <div>
        <label className="text-xs font-medium block mb-1">Name des Bausteins</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="baustein-name-feld" className="w-full border rounded px-3 py-2 text-sm font-semibold bg-background" />
      </div>
      <div>
        <label className="text-xs font-medium block mb-1">Stichwörter</label>
        <div className="flex flex-wrap gap-1 mb-1">
          {stich.map((w) => (
            <span key={w} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-primary/40 bg-card">
              {w}<button type="button" onClick={() => setStich(stich.filter((x) => x !== w))} aria-label={`Stichwort ${w} entfernen`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <input value={neu} list="baustein-stichwoerter" onChange={(e) => setNeu(e.target.value)} placeholder="Stichwort eintragen, Enter"
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); stichZu(neu); } }}
            data-testid="baustein-stichwort-feld" className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm bg-background" />
          <button type="button" onClick={() => stichZu(neu)} className="px-2 border rounded text-sm" aria-label="Stichwort hinzufügen"><Plus className="w-4 h-4" /></button>
        </div>
        <datalist id="baustein-stichwoerter">{alleWoerter.map((w) => <option key={w} value={w} />)}</datalist>
      </div>
      <div>
        <p className="text-xs font-medium mb-1">Positionen</p>
        <div className="space-y-2">
          {pos.map((p, i) => (
            <div key={i} className="flex flex-wrap items-start gap-2 border rounded-sm p-2 bg-card">
              {p.type === "titel" ? (
                <input value={p.description} onChange={(e) => aendere(i, "description", e.target.value)} className="flex-1 min-w-0 border rounded px-2 py-1 text-sm font-bold bg-background" placeholder="Titel..." />
              ) : (
                <>
                  <textarea value={p.description} onChange={(e) => aendere(i, "description", e.target.value)} className="w-full sm:flex-1 min-w-0 border rounded px-2 py-1 text-sm bg-background" rows={Math.min(8, Math.max(2, String(p.description || "").split("\n").length + 1))} />
                  <input type="number" step="0.01" value={p.quantity} onChange={(e) => aendere(i, "quantity", parseFloat(e.target.value) || 0)} className="w-16 border rounded px-2 py-1 text-sm text-right bg-background" aria-label="Menge" />
                  <input value={p.unit} onChange={(e) => aendere(i, "unit", e.target.value)} className="w-16 border rounded px-2 py-1 text-sm text-center bg-background" aria-label="Einheit" />
                  <input type="number" step="0.01" value={p.price_net} onChange={(e) => aendere(i, "price_net", parseFloat(e.target.value) || 0)} className="w-20 border rounded px-2 py-1 text-sm text-right bg-background" aria-label="Einzelpreis" />
                </>
              )}
              <button type="button" onClick={() => setPos(pos.filter((_, k) => k !== i))} className="p-1 text-muted-foreground hover:text-destructive" aria-label="Position entfernen"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" onClick={speichern} disabled={busy} data-testid="btn-baustein-speichern" className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-sm disabled:opacity-50">Speichern</button>
        <button type="button" onClick={onFertig} className="px-4 py-2 text-sm text-muted-foreground">Abbrechen</button>
        <button type="button" data-testid={`delete-block-${b.id}`} className="ml-auto px-3 py-2 text-sm text-destructive border border-destructive/40 rounded-sm inline-flex items-center gap-1"
          onClick={() => { if (window.confirm(`Baustein "${b.name}" löschen?`)) onLoeschen(b); }}>
          <Trash2 className="w-4 h-4" /> Löschen
        </button>
      </div>
    </div>
  );
};

export const useSchmal = (grenze = 640) => {
  const [schmal, setSchmal] = useState(() => typeof window !== "undefined" && window.innerWidth < grenze);
  useEffect(() => {
    const f = () => setSchmal(window.innerWidth < grenze);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, [grenze]);
  return schmal;
};

// ---------------------------------------------------------------- die Ansicht
const BausteineAnsicht = ({ bloecke: bloeckeProp, onNeuLaden, modus = "leiste", onEinfuegen, ziel = null, onEigenesFenster }) => {
  const [eigene, setEigene] = useState(null);
  const bloecke = useMemo(() => bloeckeProp ?? eigene ?? [], [bloeckeProp, eigene]);
  const neuLaden = useCallback(async () => {
    try {
      const r = await api.get("/leistungsbloecke");
      if (bloeckeProp === undefined) setEigene(r.data);
      onNeuLaden?.(r.data);
    } catch { /* egal */ }
  }, [bloeckeProp, onNeuLaden]);
  useEffect(() => {
    if (bloeckeProp !== undefined) return undefined;
    neuLaden();
    window.addEventListener("focus", neuLaden);   // eigenes Fenster: beim Zurückkehren frisch laden
    return () => window.removeEventListener("focus", neuLaden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fenster = modus === "fenster";
  const handy = modus === "handy";
  const maxVergleich = handy ? 2 : 6;
  const [suche, setSuche] = useState("");
  const [chip, setChip] = useState("");
  const [sort, setSort] = useState("zuletzt");
  const [offen, setOffen] = useState({});       // id -> true/false (von Hand); sonst automatisch (Treffer im Text)
  const [haken, setHakenAlle] = useState({});   // id -> Set der angehakten Positionen (fehlt = alle)
  const [vergleich, setVergleich] = useState([]);
  const [bearbeiten, setBearbeiten] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aktiv, setAktiv] = useState(null);      // Fenster: angeklickter Baustein (rechts ganz sichtbar)
  const [einzeln, setEinzeln] = useState(false); // Fenster: Karte angeklickt -> Einzelansicht statt Vergleich
  const waehle = (id) => { setAktiv(id); setEinzeln(true); setBearbeiten(null); };
  const schmalFenster = useSchmal(768);
  const [listeBreite, setListeBreite] = useState(() => {
    try { const v = parseInt(window.localStorage.getItem("bausteine_fenster_liste_breite"), 10); return Number.isFinite(v) ? Math.min(Math.max(v, 240), Math.round(window.innerWidth * 0.6)) : 340; } catch { return 340; }
  });
  const listeZiehen = (e) => {
    e.preventDefault();
    const grenze = (x) => Math.min(Math.max(x, 240), Math.round(window.innerWidth * 0.6));
    const bewegen = (ev) => setListeBreite(grenze(ev.clientX));
    const ende = (ev) => {
      window.removeEventListener("pointermove", bewegen); window.removeEventListener("pointerup", ende);
      const w = grenze(ev.clientX);
      setListeBreite(w);
      try { window.localStorage.setItem("bausteine_fenster_liste_breite", String(w)); } catch { /* egal */ }
    };
    window.addEventListener("pointermove", bewegen); window.addEventListener("pointerup", ende);
  };

  const woerter = useMemo(() => suchWoerter(suche), [suche]);
  const stichwoerter = useMemo(() => alleStichwoerter(bloecke), [bloecke]);
  const sichtbar = useMemo(() => sortiere(bloecke.filter((b) => passt(b, woerter) && (!chip || hatStichwort(b, chip))), sort), [bloecke, woerter, chip, sort]);
  const setHaken = (id) => (set) => setHakenAlle((h) => ({ ...h, [id]: set }));
  const markiertAnzahl = (b) => (haken[b.id] ? haken[b.id].size : (b.positions || []).length);

  const einfuegen = async (b, nurMarkierte) => {
    const set = haken[b.id];
    const liste = (b.positions || []).filter((_, i) => !nurMarkierte || !set || set.has(i));
    if (!liste.length) { toast.warning("Bitte mindestens eine Position markieren"); return; }
    if (busy) return;
    setBusy(true);
    try {
      const ok = await onEinfuegen?.(b, liste);
      if (ok === false) return;
      try { await api.post(`/leistungsbloecke/${b.id}/benutzt`); } catch { /* Zählung ist nur Komfort */ }
      neuLaden();
    } finally { setBusy(false); }
  };
  const loeschen = async (b) => {
    try {
      await api.delete(`/leistungsbloecke/${b.id}`);
      setVergleich((v) => v.filter((x) => x !== b.id));
      setBearbeiten(null);
      toast.success("Baustein gelöscht");
      neuLaden();
    } catch { toast.error("Fehler beim Löschen"); }
  };
  const vergleichUmschalten = (id) => {
    if (vergleich.includes(id)) { setVergleich(vergleich.filter((x) => x !== id)); return; }
    if (vergleich.length >= maxVergleich) { toast.warning(`Höchstens ${maxVergleich} Bausteine im Vergleich`); return; }
    setVergleich([...vergleich, id]);
  };

  const knoepfe = (b) => (
    <div className="flex flex-wrap gap-2 mt-3">
      <button type="button" onClick={() => einfuegen(b, false)} disabled={busy} data-testid={`insert-block-${b.id}`}
        className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-sm hover:bg-primary/90 disabled:opacity-50">
        <Plus className="w-3 h-3 inline mr-1" />{fenster ? `In ${ziel?.label || "Beleg"} einfügen` : "Ganz einfügen"}
      </button>
      <button type="button" onClick={() => einfuegen(b, true)} disabled={busy || markiertAnzahl(b) === 0} data-testid={`insert-marked-${b.id}`}
        className="px-3 py-1.5 border text-xs font-medium rounded-sm hover:bg-muted disabled:opacity-50">
        Nur markierte ({markiertAnzahl(b)})
      </button>
      <button type="button" onClick={() => { setOffen((o) => ({ ...o, [b.id]: true })); setBearbeiten(bearbeiten === b.id ? null : b.id); }} data-testid={`edit-block-${b.id}`}
        className="px-3 py-1.5 border text-xs font-medium rounded-sm hover:bg-muted">Bearbeiten</button>
      {!fenster && (
        <button type="button" onClick={() => vergleichUmschalten(b.id)} data-testid={`vergleichen-${b.id}`}
          className={`px-3 py-1.5 border text-xs font-medium rounded-sm ${vergleich.includes(b.id) ? "bg-primary/10 border-primary" : "hover:bg-muted"}`}>
          {vergleich.includes(b.id) ? "✓ im Vergleich" : "vergleichen"}
        </button>
      )}
    </div>
  );

  const koerper = (b, inSpalte = false) => (
    bearbeiten === b.id && (fenster || !inSpalte)
      ? <Bearbeiten b={b} alleWoerter={stichwoerter} onFertig={() => setBearbeiten(null)} onGespeichert={neuLaden} onLoeschen={loeschen} />
      : <><Inhalt b={b} woerter={woerter} haken={haken[b.id]} setHaken={setHaken(b.id)} />{knoepfe(b)}</>
  );

  const meta = (b) => (
    <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground mt-0.5">
      <span>{(b.positions || []).length} Positionen</span>
      {(b.stichwoerter || []).length > 0 && <span>·</span>}
      {(b.stichwoerter || []).map((w) => <span key={w} className="px-1.5 py-0 text-[10px] rounded-full border border-primary/40 text-foreground">{w}</span>)}
      {b.zuletzt_benutzt && <span>· zuletzt benutzt {datumKurz(b.zuletzt_benutzt)}</span>}
    </div>
  );

  const spalte = (b) => (
    <div className="border rounded-sm p-3 bg-card min-w-0" data-testid={`spalte-${b.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-semibold text-sm break-words"><Markiert text={b.name} woerter={woerter} /></h4>
        <button type="button" onClick={() => vergleichUmschalten(b.id)} aria-label="aus dem Vergleich nehmen" className="p-0.5 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-xs text-muted-foreground">{euro(b.summe_netto)} netto{(b.stichwoerter || []).length ? ` · Stichwörter: ${b.stichwoerter.join(", ")}` : ""}</p>
      {koerper(b, true)}
    </div>
  );

  const vergleichBausteine = vergleich.map((id) => bloecke.find((b) => b.id === id)).filter(Boolean);
  const vergleichBereich = () => (
    <div className={fenster ? "" : "border-2 border-primary/40 rounded-sm p-2 mb-3"} data-testid="vergleich-bereich">
      {!fenster && (
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">Vergleich ({vergleichBausteine.length})</p>
          <button type="button" onClick={() => setVergleich([])} className="text-xs text-muted-foreground underline">Vergleich schließen</button>
        </div>
      )}
      {vergleichBausteine.length < 2 && <p className="text-xs text-muted-foreground mb-2">{vergleichBausteine.length === 0 ? (handy ? "Haken setzen: bis zu 2 Bausteine stehen dann untereinander." : `Haken links setzen: bis zu ${maxVergleich} Bausteine stehen dann nebeneinander – bei vielen seitlich blättern.`) : "Wählen Sie noch einen Baustein zum Vergleich."}</p>}
      <div className="grid gap-3" data-testid="vergleich-raster" style={{ gridTemplateColumns: handy || vergleichBausteine.length < 2 ? "1fr" : fenster ? `repeat(${vergleichBausteine.length}, minmax(380px, 1fr))` : `repeat(auto-fit, minmax(260px, 1fr))` }}>
        {vergleichBausteine.map((b) => <div key={b.id} className="min-w-0">{spalte(b)}</div>)}
      </div>
    </div>
  );

  // ---- Kopf: Suche, Stichwörter, Sortierung, Zähler
  const Kopf = (
    <div className="space-y-2">
      {modus === "leiste" && onEigenesFenster && (
        <div className="flex justify-end">
          <button type="button" onClick={onEigenesFenster} data-testid="btn-eigenes-fenster" className="px-2.5 py-1 border rounded-sm text-xs hover:bg-muted inline-flex items-center gap-1">
            <ExternalLink className="w-3.5 h-3.5" /> Eigenes Fenster
          </button>
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Baustein suchen – auch im Text der Positionen" data-testid="baustein-suche"
          className="w-full h-10 pl-8 pr-8 border-2 border-primary/60 rounded-sm text-sm bg-background focus:outline-none focus:border-primary" />
        {suche && <button type="button" onClick={() => setSuche("")} aria-label="Suche leeren" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"><X className="w-4 h-4" /></button>}
      </div>
      {stichwoerter.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="baustein-chips">
          <Stichwort w={`alle (${sichtbar.length} von ${bloecke.length})`} aktiv={!chip} onClick={() => setChip("")} testid="chip-alle" />
          {stichwoerter.map((w) => <Stichwort key={w} w={w} aktiv={chip && norm(chip) === norm(w)} onClick={() => setChip(chip && norm(chip) === norm(w) ? "" : w)} testid={`chip-${w}`} />)}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span className="whitespace-nowrap" data-testid="baustein-zaehler">{sichtbar.length} von {bloecke.length}</span>
        <label className="flex items-center gap-1 whitespace-nowrap">Sortierung
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="baustein-sortierung" className="h-7 border rounded pl-2 pr-6 min-w-[9.5rem] bg-background text-foreground">
            {SORTIERUNGEN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
    </div>
  );

  const Leer = (
    <p className="text-xs text-muted-foreground text-center py-6" data-testid="baustein-leer">
      {bloecke.length === 0 ? (
        <>Noch keine Bausteine vorhanden.<br />So legen Sie einen an: Im Beleg die Häkchen vor den Positionen setzen, unten einen Namen eintragen und „Als Baustein speichern“ drücken.</>
      ) : "Keine Bausteine gefunden. Suche oder Stichwort ändern."}
    </p>
  );

  // ---- Fenster: Liste links (kompakt), gewählter Baustein rechts über die ganze Breite; 2-3 Haken = Vergleich
  if (fenster) {
    const gewaehlt = sichtbar.find((b) => b.id === aktiv) || sichtbar[0] || null;   // erster Treffer automatisch
    const zeigeVergleich = vergleich.length >= 2 && !einzeln;
    return (
      <div className="flex flex-col md:flex-row h-full min-h-0 w-full max-w-full overflow-x-hidden" data-testid="bausteine-ansicht">
        <aside className="md:shrink-0 min-w-0 p-3 overflow-y-auto overflow-x-hidden space-y-3 md:h-full md:border-r-0" style={{ width: schmalFenster ? undefined : listeBreite }} data-testid="fenster-liste">
          {Kopf}
          <div className="space-y-2">
            {sichtbar.map((b) => {
              const im = vergleich.includes(b.id);
              const aktivKarte = gewaehlt?.id === b.id && !zeigeVergleich;
              const treffer = woerter.length ? (b.positions || []).filter((p) => p.type !== "titel" && woerter.some((w) => norm(p.description).includes(w))) : [];
              return (
                <div key={b.id} role="button" tabIndex={0} onClick={() => waehle(b.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); waehle(b.id); } }}
                  data-testid={`baustein-${b.id}`} className={`border rounded-sm p-2.5 bg-card cursor-pointer ${aktivKarte ? "border-primary ring-1 ring-primary bg-primary/5" : "hover:border-primary/40"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm break-words min-w-0"><Markiert text={b.name} woerter={woerter} /></p>
                    <p className="text-xs font-mono whitespace-nowrap shrink-0 pt-0.5">{euro(b.summe_netto)}</p>
                  </div>
                  {meta(b)}
                  {treffer.length > 0 && (
                    <p className="mt-1 text-xs text-foreground/80 truncate" data-testid="treffer-zeilen" title={treffer.map((p) => p.description).join(" | ")}>
                      {treffer.length > 1 ? `${treffer.length} Treffer: ` : "Treffer: "}<Markiert text={(String(treffer[0].description).split("\n").find((z) => woerter.some((w) => norm(z).includes(w))) || "").trim()} woerter={woerter} />
                    </p>
                  )}
                  <label className="mt-1.5 inline-flex items-center gap-1.5 text-xs cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={im} onChange={() => { setEinzeln(false); vergleichUmschalten(b.id); }} data-testid={`vergleich-${b.id}`} className="w-4 h-4" />
                    vergleichen
                  </label>
                </div>
              );
            })}
            {sichtbar.length === 0 && Leer}
            <p className="text-[11px] text-muted-foreground">☑ vergleichen: 2 bis {maxVergleich} Bausteine stehen rechts nebeneinander, bei vielen blättern Sie dort seitlich.</p>
          </div>
        </aside>
        {!schmalFenster && (
          <div onPointerDown={listeZiehen} data-testid="fenster-ziehgriff" title="Breite ziehen" className="w-2 shrink-0 cursor-col-resize bg-border/60 hover:bg-primary/50 touch-none" />
        )}
        <main className="flex-1 min-w-0 p-3 overflow-auto md:h-full" data-testid="vergleich-fenster">
          {zeigeVergleich ? vergleichBereich() : gewaehlt ? (
            <div data-testid="detail-fenster" className="w-full">
              {vergleich.length === 1 && <p className="text-xs text-muted-foreground mb-2">Zum Vergleichen noch einen weiteren Baustein anhaken.</p>}
              <div className="border rounded-sm p-3 bg-card min-w-0" data-testid={`spalte-${gewaehlt.id}`}>
                <h3 className="font-semibold text-base break-words"><Markiert text={gewaehlt.name} woerter={woerter} /></h3>
                <p className="text-xs text-muted-foreground">{euro(gewaehlt.summe_netto)} netto{(gewaehlt.stichwoerter || []).length ? ` · Stichwörter: ${gewaehlt.stichwoerter.join(", ")}` : ""}</p>
                {koerper(gewaehlt, true)}
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground p-6 text-center">Kein Baustein ausgewählt.</p>}
        </main>
      </div>
    );
  }

  // ---- Leiste / Handy / Einstellungen
  return (
    <div className="space-y-3" data-testid="bausteine-ansicht">
      {Kopf}
      {vergleich.length > 0 && vergleichBereich()}
      <div className={`space-y-2 ${modus === "einstellungen" ? "md:grid md:grid-cols-2 md:gap-3 md:space-y-0 md:items-start" : ""}`}>
        {sichtbar.map((b) => {
          const aufgeklappt = offen[b.id] ?? trefferInPositionen(b, woerter);
          return (
            <div key={b.id} data-testid={`baustein-${b.id}`} className={`border rounded-sm bg-card transition-colors ${aufgeklappt ? "border-primary/60" : "hover:border-primary/30"}`}>
              <button type="button" onClick={() => setOffen({ ...offen, [b.id]: !aufgeklappt })} aria-expanded={aufgeklappt} data-testid={`baustein-kopf-${b.id}`} className="w-full text-left p-3 flex items-start gap-1.5">
                {aufgeklappt ? <ChevronDown className="w-4 h-4 mt-0.5 shrink-0" /> : <ChevronRight className="w-4 h-4 mt-0.5 shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-sm break-words"><Markiert text={b.name} woerter={woerter} /></span>
                    <span className="text-sm font-mono whitespace-nowrap">{euro(b.summe_netto)} netto</span>
                  </span>
                  {meta(b)}
                </span>
              </button>
              {aufgeklappt && <div className="px-3 pb-3 border-t">{koerper(b)}</div>}
            </div>
          );
        })}
        {sichtbar.length === 0 && Leer}
      </div>
    </div>
  );
};

export { BausteineAnsicht };
